/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Box, Button as MUIButton, SvgIconProps, Theme,
  ThemeProvider,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import type { ButtonProps } from './Button.types';
import { buttonSizeSpecs, buttonVariantStyles } from './Button.styles';
import theme from '../../../assets/theme';

const Button: React.FC<ButtonProps> = ({
  size = 'm',
  customVariant = 'primary',
  'data-testid': dataTestId = 'optimus-button',
  leadingIcon,
  trailingIcon,
  disabled = false,
  onClick,
  buttonText,
  fullWidth = true,
  width,
  minWidth,
  maxWidth,
  height,
  minHeight,
  maxHeight,
  iconProps,
  iconSpace,
  sx,
  type,
  disableRipple = false,
  ...props
}) => {
  /*
  ** Helper to safely render icons passed as either JSX (<Icon />) or component refs (Icon)
  */
  const renderIcon = (
    icon?: React.ReactNode | React.ElementType<SvgIconProps> | undefined,
    iconPassedProps?: SvgIconProps,
  ) => {
    if (!icon) return null;
    // If JSX (<Icon />), allow explicit element props to win and return
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, { ...iconPassedProps });
    }
    // Else instantiate it from the component ref (Icon)
    const IconComp = icon as React.ElementType<SvgIconProps>;
    return <IconComp {...iconPassedProps} />;
  };

  /*
  ** Helper to normalizes whatever props.children is to safely inspect / map over
  ** Obs: Children is set to be a string in ButtonProps, but we can change it to ReactNode if needed
  ** This allows for more flexibility in the content, like passing JSX elements or fragments
  */
  const childArray = React.Children.toArray(buttonText);
  const hasElements =
    childArray.length > 0 &&
    !(childArray.length === 1 &&
      typeof childArray[0] === 'string' &&
      childArray[0].trim() === '');

  const isIconOnly = !hasElements && (!!leadingIcon || !!trailingIcon);

  const dimensionOverrides: SystemStyleObject<Theme> = {
    ...(fullWidth ? { width: '100%' } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(maxWidth !== undefined ? { maxWidth } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(minHeight !== undefined ? { minHeight } : {}),
    ...(maxHeight !== undefined ? { maxHeight } : {}),
    ...(iconSpace ? { gap: iconSpace } : {}),
    ...(isIconOnly ? { gap: 0 } : {}),
  };

  return (
    <MUIButton
      data-testid={dataTestId}
      variant="contained"
      color="inherit"
      disableElevation
      disableRipple={disableRipple}
      disableFocusRipple={disableRipple}
      disabled={disabled}
      onClick={(e) => onClick?.(e)}
      sx={(muiTheme: Theme) => ({
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...buttonSizeSpecs[size],
        ...buttonVariantStyles[customVariant](muiTheme),
        // To override props need to be add here to take precedence
        ...dimensionOverrides,
        ...sx,
      })}
      type={type}
      {...props}
    >
      {leadingIcon && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderIcon(leadingIcon, iconProps)}
        </Box>
      )}

      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {buttonText}
      </Box>

      {trailingIcon && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderIcon(trailingIcon, iconProps)}
        </Box>
      )}
    </MUIButton>
  );
};

const OptimusButton: React.FC<React.ComponentProps<typeof Button>> = (props) => (
  <ThemeProvider theme={theme}>
    <Button {...props} />
  </ThemeProvider>
);

export default OptimusButton;
